/**
 * ProductRegistrationModal 共通スタイル。
 *
 * 以前は styled-components を export していたが、Tailwind へ移行したため
 * 「完全な Tailwind クラス文字列の定数」を export するモジュールになっている。
 * 定数どうしはテンプレートリテラルで連結してよい（各定数がリテラルなので purge に拾われる）。
 * keyframes（sweep / ctaGlow）は globals.css 末尾に `prd-sweep` / `prd-cta-glow` として移設済み。
 */

/** ウィザード本体のラッパー（ModalContent）。 */
export const MODAL_CONTENT =
  'flex flex-col pt-7 px-12 pb-0 min-h-[min(560px,calc(100dvh_-_240px))] bg-[#F8F7F3] ' +
  '[&>form]:flex [&>form]:flex-1 [&>form]:min-h-[inherit] [&>form]:flex-col ' +
  'max-[767px]:min-h-[calc(100dvh_-_120px)] max-[767px]:pt-[22px] max-[767px]:px-[18px]';

/** 入力1件ぶんのカード（FormGroup）。 */
export const FORM_GROUP =
  'flex flex-col gap-2 mb-[14px] py-[14px] px-4 border border-warm-border rounded-[10px] bg-white shadow-[0_1px_2px_rgba(31,25,39,0.03)]';

/** FormGroup 内のラベル（Label / label 要素）。 */
export const LABEL = 'flex items-center gap-1 text-dark text-[13px] font-bold';

/** 必須マーク（RequiredBadge / span 要素）。 */
export const REQUIRED_BADGE = 'text-red-500 text-[13px]';

/** テキスト入力（Input / input 要素）。 */
export const INPUT =
  'w-full min-h-[44px] py-[11px] px-[13px] border border-warm-border rounded-lg bg-white text-dark text-[14px] ' +
  'transition-[border-color,box-shadow] duration-200 ease-[ease] ' +
  'focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(97,111,201,0.16)] ' +
  'disabled:bg-[#f5f5f5] disabled:cursor-not-allowed disabled:text-[#B0AC9F] placeholder:text-[#B0AC9F]';

/** 補足テキスト（HelpText / span 要素）。 */
export const HELP_TEXT = 'mt-1 text-gray-500 text-[12px] leading-[1.6]';

/** エラー表示（ErrorMessage / div 要素）。 */
export const ERROR_MESSAGE = 'flex items-center gap-1 mt-1 text-red-500 text-[13px]';

/**
 * sticky フッター（ButtonGroup）。
 * 左右 48px（モバイルは 18px）を負マージンで打ち消してモーダル幅いっぱいに広げる。
 */
export const BUTTON_GROUP =
  'sticky bottom-0 z-[3] flex gap-3 justify-end mt-auto -mx-12 pt-5 px-12 pb-4 bg-[#F8F7F3] border-t border-t-warm-border ' +
  'max-[767px]:flex-col-reverse max-[767px]:gap-2 max-[767px]:-mx-[18px] max-[767px]:py-4 max-[767px]:px-[18px] max-[767px]:[&_button]:w-full';

export type ButtonVariant = 'primary' | 'secondary' | 'outline';

/** Button 共通（バリアント非依存）。 */
export const BUTTON_BASE =
  'min-h-[42px] px-[18px] rounded-lg text-[13px] font-bold cursor-pointer transition-all duration-200 ease-[ease] ' +
  'disabled:opacity-[0.46] disabled:cursor-not-allowed disabled:shadow-none';

/** Button の3バリアント。border 系は base に置かず各バリアントで完結させる（border-0 と border の出力順対策）。 */
export const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    'border-0 bg-primary text-white shadow-[0_2px_8px_rgba(97,111,201,0.22)] ' +
    'enabled:hover:bg-primary-hover enabled:hover:-translate-y-px',
  secondary:
    'border border-warm-border bg-white text-dark ' +
    'enabled:hover:bg-[#ECEEFB] enabled:hover:border-[#AEB5DE] enabled:hover:text-primary-hover',
  outline:
    'border border-warm-border bg-white text-gray-500 ' +
    'enabled:hover:border-primary enabled:hover:text-primary',
};

/** Button の className を組み立てる（完全なクラス文字列定数どうしの連結）。 */
export const buttonClass = (variant: ButtonVariant = 'primary'): string =>
  `${BUTTON_BASE} ${BUTTON_VARIANT[variant]}`;

/** ウィザード見出し（WizardTitle / div 要素）。 */
export const WIZARD_TITLE = 'text-dark text-[20px] font-extrabold tracking-[0]';

/** ウィザード小見出し（WizardSubtitle / p 要素）。 */
export const WIZARD_SUBTITLE =
  'mx-0 mt-[7px] mb-[22px] text-gray-500 text-[13px] leading-[1.7]';

/** 完了画面（Step4Complete）。 */
export const SUCCESS_CONTAINER = 'flex flex-col items-center gap-5 p-6';
export const SUCCESS_ICON =
  'grid place-items-center w-[76px] h-[76px] rounded-full bg-[rgba(68,194,164,0.12)] text-success text-[44px] font-extrabold leading-none';
export const SUCCESS_MESSAGE = 'm-0 text-dark text-[18px] font-bold text-center';
export const SUCCESS_DESCRIPTION = 'm-0 text-gray-500 text-[13px] leading-[1.7] text-center';

/**
 * 完了画面の「このまま無料でバナーを10枚作る」大型キラキラCTA。
 * ::after の光沢は prd-sweep、本体のグローは prd-cta-glow（globals.css）。
 */
export const SPARKLE_CTA =
  'relative overflow-hidden flex items-center justify-center gap-2.5 w-[min(560px,100%)] min-h-[66px] ' +
  'mx-auto mt-1 mb-0 px-6 border-0 rounded-[15px] bg-[linear-gradient(115deg,#4f5db5,#7c3aed)] ' +
  'text-white text-[1.12rem] font-extrabold tracking-[0.01em] cursor-pointer ' +
  'shadow-[0_14px_34px_rgba(124,58,237,0.34)] animate-[prd-cta-glow_2.2s_ease-in-out_infinite] ' +
  "after:content-[''] after:absolute after:top-[-20%] after:bottom-[-20%] after:left-0 after:right-auto after:w-[34%] " +
  'after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent)] after:translate-x-[-140%] ' +
  'after:animate-[prd-sweep_2.6s_ease-in-out_infinite] ' +
  'enabled:hover:-translate-y-px disabled:opacity-65 disabled:cursor-wait disabled:animate-none ' +
  'motion-reduce:animate-none motion-reduce:after:animate-none';

/** SparkleCTA 下の補足テキスト（初回限定・無料の後押し）。 */
export const SPARKLE_CTA_NOTE = 'mx-0 mt-2.5 mb-0 text-gray-500 text-[12px] text-center';

/** 完了画面の商品サマリー。 */
export const PRODUCT_INFO = 'w-full mt-2 p-4 border border-warm-border rounded-xl bg-warm-bg';
export const PRODUCT_INFO_ITEM = 'flex flex-col gap-1 [&+&]:mt-3';
export const PRODUCT_INFO_LABEL = 'text-gray-500 text-[12px] font-bold';
export const PRODUCT_INFO_VALUE = 'text-dark text-[14px] [word-break:break-word]';

/** ProgressIndicator（旧 colors オブジェクトの参照先だった値も含む）。 */
export const PROGRESS_CONTAINER =
  'flex items-center justify-center m-0 pt-[18px] px-12 pb-0 bg-[#F8F7F3] max-[767px]:pt-4 max-[767px]:px-[18px]';

export const PROGRESS_STEP_CIRCLE_BASE =
  'flex items-center justify-center w-[30px] h-[30px] rounded-full text-[12px] font-extrabold shrink-0 ' +
  'transition-all duration-200 ease-[ease] max-[767px]:w-7 max-[767px]:h-7 max-[767px]:text-[11px]';

export const PROGRESS_STEP_CIRCLE_COMPLETED =
  'bg-[#ECEEFB] text-primary border border-[#AEB5DE]';
export const PROGRESS_STEP_CIRCLE_CURRENT =
  'bg-primary text-white border border-primary shadow-[0_4px_12px_rgba(97,111,201,0.18)]';
export const PROGRESS_STEP_CIRCLE_DEFAULT =
  'bg-white text-gray-500 border border-warm-border';

export const PROGRESS_STEP_LINE_BASE =
  'flex-1 max-w-[104px] h-px mx-2.5 my-0 transition-[background-color] duration-200 ease-[ease] max-[767px]:mx-[5px]';
export const PROGRESS_STEP_LINE_COMPLETED = 'bg-primary';
export const PROGRESS_STEP_LINE_DEFAULT = 'bg-warm-border';
